import React, { useState, useEffect, useRef } from 'react';
import SelectField from '../common/SelectField.js';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from '../common/Loader';
import tripContractService from '../../services/tripContractService'
import { Package, TrendingUp, Calendar, CreditCard, Download, ChevronDown, Filter, IndianRupee, Clock, CheckCircle, AlertCircle, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AnalyticsTable } from './common/index.js';
import { formatCurrencyPaise } from '../../utils/formatCurrency.js';

const formatCurrencyWhole = (v) => formatCurrencyPaise(v, '₹', { alwaysPaise: true });

const TripContractAnalytics = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [contracts, setContracts] = useState([]);
  const [filteredContracts, setFilteredContracts] = useState([]);
  const [totals, setTotals] = useState({
    totalContracts: 0,
    totalCost: 0,
    totalPaid: 0,
    totalPending: 0,
    activeContracts: 0,
    expiredContracts: 0,
    totalTrips: 0,
    totalDistance: 0
  });

  const [showFilters, setShowFilters] = useState(false);
  const [dateFilter, setDateFilter] = useState('all');
  const [customDates, setCustomDates] = useState({ from_date: '', to_date: '' });
  const [statusFilter, setStatusFilter] = useState('all');
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const dateDropdownRef = useRef(null);

  const filterOptions = [
    { value: 'current_month', label: 'Current Month' },
    { value: 'last_3_months', label: 'Last 3 Months' },
    { value: 'last_6_months', label: 'Last 6 Months' },
    { value: 'last_year', label: 'Last Year' },
    { value: 'all', label: 'All Time' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'expired', label: 'Expired' },
    { value: 'expiring_soon', label: 'Expiring Soon' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dateDropdownRef.current && !dateDropdownRef.current.contains(event.target)) {
        setIsDateDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    applyFilters();
  }, [contracts, dateFilter, customDates, statusFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await tripContractService.getAll(1, 1000, { all: 'true' });
      setContracts(response.data.data || response.data || []);
      setFilteredContracts(response.data.data || response.data || []);
      calculateTotals(response.data.data || response.data || []);
    } catch (error) {
      console.error('Error fetching trip contracts:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = (contractsData) => {
    const today = new Date();
    const totals = contractsData.reduce((acc, contract) => {
      acc.totalContracts += 1;
      acc.totalCost += contract.total_cost || contract.cost || 0;
      acc.totalPaid += contract.total_paid || contract.paid || 0;
      acc.totalPending += ((contract.total_cost || contract.cost || 0) - (contract.total_paid || contract.paid || 0));
      acc.totalTrips += contract.total_trips || 0;
      acc.totalDistance += contract.total_distance || 0;

      const toDate = new Date(contract.to_date || contract.end_date);
      if (toDate > today) {
        acc.activeContracts += 1;
      } else {
        acc.expiredContracts += 1;
      }
      return acc;
    }, {
      totalContracts: 0,
      totalCost: 0,
      totalPaid: 0,
      totalPending: 0,
      activeContracts: 0,
      expiredContracts: 0,
      totalTrips: 0,
      totalDistance: 0
    });

    setTotals(totals);
  };

  const getDateFilterLabel = () => {
    const option = filterOptions.find(opt => opt.value === dateFilter);
    return option ? option.label : 'Select Period';
  };

  const calculateDateRange = (filter, customFrom, customTo) => {
    const today = new Date();
    let startDate, endDate;

    switch (filter) {
      case 'current_month':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      case 'last_3_months':
        startDate = new Date(today.getFullYear(), today.getMonth() - 3, 1);
        endDate = today;
        break;
      case 'last_6_months':
        startDate = new Date(today.getFullYear(), today.getMonth() - 6, 1);
        endDate = today;
        break;
      case 'last_year':
        startDate = new Date(today.getFullYear() - 1, today.getMonth(), 1);
        endDate = today;
        break;
      case 'custom':
        if (customFrom && customTo) {
          startDate = new Date(customFrom);
          endDate = new Date(customTo);
        }
        break;
      default:
        return null;
    }

    if (startDate && endDate) {
      return {
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0]
      };
    }
    return null;
  };

  const getContractStatus = (contract) => {
    const today = new Date();
    const toDate = new Date(contract.to_date || contract.end_date);
    const daysLeft = Math.ceil((toDate - today) / (1000 * 60 * 60 * 24));

    if (toDate < today) return 'expired';
    if (daysLeft <= 5) return 'expiring_soon';
    return 'active';
  };

  const applyFilters = () => {
    let filtered = [...contracts];

    // Apply date filter
    if (dateFilter !== 'all') {
      const range = calculateDateRange(dateFilter, customDates.from_date, customDates.to_date);
      if (range) {
        filtered = filtered.filter(contract => {
          const contractDate = new Date(contract.created_at).toISOString().split('T')[0];
          return contractDate >= range.start_date && contractDate <= range.end_date;
        });
      }
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(contract => getContractStatus(contract) === statusFilter);
    }

    setFilteredContracts(filtered);
    calculateTotals(filtered);
  };

  const handleDateFilterChange = (value) => {
    if (value === 'custom') {
      setDateFilter(value);
    } else {
      setDateFilter(value);
      setIsDateDropdownOpen(false);
      setCustomDates({ from_date: '', to_date: '' });
    }
  };

  const handleCustomApply = () => {
    if (customDates.from_date && customDates.to_date) {
      setDateFilter('custom');
      setIsDateDropdownOpen(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
          <div className="p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Trip Contract Analytics</h1>
                <p className="text-gray-600 mt-2">Monitor trip contract performance and delivery metrics</p>
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-4 py-2 bg-sky-50 text-sky-700 border border-sky-200 rounded-xl hover:bg-sky-100 transition"
              >
                <Filter size={20} />
                Filters
              </button>
            </div>

            {/* Filters */}
            {showFilters && (
              <div className="bg-white p-4 rounded-lg mb-6 shadow-sm border border-gray-200">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {/* Date Filter */}
                  <div className="relative" ref={dateDropdownRef}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
                    <button
                      onClick={() => setIsDateDropdownOpen(!isDateDropdownOpen)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between hover:border-gray-400"
                    >
                      {getDateFilterLabel()}
                      <ChevronDown size={20} />
                    </button>
                    {isDateDropdownOpen && (
                      <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-300 rounded-lg shadow-lg z-10">
                        {filterOptions.map(option => (
                          <button
                            key={option.value}
                            onClick={() => handleDateFilterChange(option.value)}
                            className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Custom Date Range */}
                  {dateFilter === 'custom' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
                        <input
                          type="date"
                          value={customDates.from_date}
                          onChange={(e) => setCustomDates({ ...customDates, from_date: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
                        <input
                          type="date"
                          value={customDates.to_date}
                          onChange={(e) => setCustomDates({ ...customDates, to_date: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                      <div className="flex items-end">
                        <button
                          onClick={handleCustomApply}
                          className="w-full px-4 py-2 bg-sky-50 text-sky-700 border border-sky-200 rounded-xl hover:bg-sky-100 transition"
                        >
                          Apply
                        </button>
                      </div>
                    </>
                  )}

                  {/* Status Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <SelectField variant="form"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      {statusOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </SelectField>
                  </div>
                </div>
              </div>
            )}

            {/* Key Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8 gap-4 mb-8">
              <div className="bg-white p-3 sm:p-6 rounded-lg shadow-sm border border-gray-200 min-w-0">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-xs sm:text-sm font-medium leading-snug">Total Contracts</p>
                    <p className="text-lg sm:text-2xl lg:text-3xl font-bold text-gray-900 mt-1 sm:mt-2 whitespace-nowrap leading-none">{totals.totalContracts}</p>
                  </div>
                  <Package className="text-blue-500 w-6 h-6 sm:w-8 sm:h-8 shrink-0" />
                </div>
              </div>

              <div className="bg-white p-3 sm:p-6 rounded-lg shadow-sm border border-gray-200 min-w-0">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-xs sm:text-sm font-medium leading-snug">Total Cost</p>
                    <p className="text-lg sm:text-2xl lg:text-3xl font-bold text-gray-900 mt-1 sm:mt-2 whitespace-nowrap leading-none">{formatCurrencyWhole(totals.totalCost)}</p>
                  </div>
                  <IndianRupee className="text-blue-500 w-6 h-6 sm:w-8 sm:h-8 shrink-0" />
                </div>
              </div>

              <div className="bg-white p-3 sm:p-6 rounded-lg shadow-sm border border-gray-200 min-w-0">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-xs sm:text-sm font-medium leading-snug">Total Paid</p>
                    <p className="text-lg sm:text-2xl lg:text-3xl font-bold text-gray-900 mt-1 sm:mt-2 whitespace-nowrap leading-none">{formatCurrencyWhole(totals.totalPaid)}</p>
                  </div>
                  <CheckCircle className="text-blue-500 w-6 h-6 sm:w-8 sm:h-8 shrink-0" />
                </div>
              </div>

              <div className="bg-white p-3 sm:p-6 rounded-lg shadow-sm border border-gray-200 min-w-0">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-xs sm:text-sm font-medium leading-snug">Pending</p>
                    <p className="text-lg sm:text-2xl lg:text-3xl font-bold text-gray-900 mt-1 sm:mt-2 whitespace-nowrap leading-none">{formatCurrencyWhole(totals.totalPending)}</p>
                  </div>
                  <Clock className="text-blue-500 w-6 h-6 sm:w-8 sm:h-8 shrink-0" />
                </div>
              </div>

              <div className="bg-white p-3 sm:p-6 rounded-lg shadow-sm border border-gray-200 min-w-0">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-xs sm:text-sm font-medium leading-snug">Active</p>
                    <p className="text-lg sm:text-2xl lg:text-3xl font-bold text-gray-900 mt-1 sm:mt-2 whitespace-nowrap leading-none">{totals.activeContracts}</p>
                  </div>
                  <TrendingUp className="text-blue-600 w-6 h-6 sm:w-8 sm:h-8 shrink-0" />
                </div>
              </div>

              <div className="bg-white p-3 sm:p-6 rounded-lg shadow-sm border border-gray-200 min-w-0">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-xs sm:text-sm font-medium leading-snug">Expired</p>
                    <p className="text-lg sm:text-2xl lg:text-3xl font-bold text-gray-900 mt-1 sm:mt-2 whitespace-nowrap leading-none">{totals.expiredContracts}</p>
                  </div>
                  <AlertCircle className="text-blue-500 w-6 h-6 sm:w-8 sm:h-8 shrink-0" />
                </div>
              </div>

              <div className="bg-white p-3 sm:p-6 rounded-lg shadow-sm border border-gray-200 min-w-0">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-xs sm:text-sm font-medium leading-snug">Total Trips</p>
                    <p className="text-lg sm:text-2xl lg:text-3xl font-bold text-gray-900 mt-1 sm:mt-2 whitespace-nowrap leading-none">{totals.totalTrips}</p>
                  </div>
                  <Package className="text-blue-500 w-6 h-6 sm:w-8 sm:h-8 shrink-0" />
                </div>
              </div>

              <div className="bg-white p-3 sm:p-6 rounded-lg shadow-sm border border-gray-200 min-w-0">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-xs sm:text-sm font-medium leading-snug">Total Distance</p>
                    <p className="text-lg sm:text-2xl lg:text-3xl font-bold text-gray-900 mt-1 sm:mt-2 whitespace-nowrap leading-none">{totals.totalDistance.toLocaleString()} km</p>
                  </div>
                  <MapPin className="text-blue-500 w-6 h-6 sm:w-8 sm:h-8 shrink-0" />
                </div>
              </div>
            </div>

            <AnalyticsTable
              title="Trip Contracts Summary"
              columns={[
                { header: 'Contract ID', align: 'left' },
                { header: 'Vehicle', align: 'left' },
                { header: 'Start Date', align: 'left' },
                { header: 'End Date', align: 'left' },
                { header: 'Total Cost', align: 'right' },
                { header: 'Paid', align: 'right', cellClassName: 'text-green-600' },
                { header: 'Trips', align: 'right' },
                { header: 'Distance', align: 'right' },
                { header: 'Status', align: 'left' },
                { header: 'Action', align: 'center' },
              ]}
              rows={filteredContracts.map((contract) => {
                const status = getContractStatus(contract);
                const statusColor = status === 'active' ? 'bg-green-100 text-green-800'
                  : status === 'expiring_soon' ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-red-100 text-red-800';
                const statusLabel = status === 'active' ? 'Active'
                  : status === 'expiring_soon' ? 'Expiring Soon'
                  : 'Expired';
                return {
                  key: contract.id,
                  cells: [
                    contract.id,
                    contract.vehicle_number || 'N/A',
                    new Date(contract.from_date).toLocaleDateString(),
                    new Date(contract.to_date).toLocaleDateString(),
                    formatCurrencyWhole((contract.total_cost || contract.cost) || 0),
                    formatCurrencyWhole((contract.total_paid || contract.paid) || 0),
                    contract.total_trips || 0,
                    `${(contract.total_distance || 0).toLocaleString()} km`,
                    <span key="status" className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${statusColor}`}>{statusLabel}</span>,
                    <button
                      key="action"
                      type="button"
                      onClick={() => navigate(`/trip-contracts/view/${contract.id}`)}
                      className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                    >
                      View
                    </button>,
                  ],
                };
              })}
              emptyMessage="No contracts found for the selected filters"
              tableMinWidth="960px"
            />
          </div>
    </DashboardLayout>
  );
};

export default TripContractAnalytics;
